import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { GROUP_COLORS } from "@/constants/promis";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { env } from "@/env";
import { usePromis } from "@/hooks/use-promis";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { PromisSymptomAggregate } from "@/types/promis";
import { ScreenContent, ScreenHeader, ScreenTitle, SegmentedControl, SinglePageTable } from "@/ui";
import { formatSymptomName } from "@/utils/promis";
import { DeltaBadge } from "./-components/delta-badge";
import { SampleBadge } from "./-components/sample-badge";
import { StatCard } from "./-components/stat-card";

type FilterType = "all" | "sufficient" | "low";

const FILTERS: { label: string; value: FilterType }[] = [
  { label: "All", value: "all" },
  { label: "Sufficient data", value: "sufficient" },
  { label: "Low N", value: "low" },
];

const columns: ColumnDef<PromisSymptomAggregate>[] = [
  {
    id: "symptomCode",
    accessorKey: "symptomCode",
    header: "Symptom",
    enableSorting: false,
    cell: ({ row }) => {
      const color = row.original.groupCode
        ? (GROUP_COLORS[row.original.groupCode] ?? "bg-content-subtle")
        : "bg-content-subtle";

      return (
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />
          {formatSymptomName(row.original.symptomCode)}
        </div>
      );
    },
  },
  {
    id: "sampleSize",
    accessorKey: "sampleSize",
    header: () => {
      return <div className="w-full text-center">Sample (N)</div>;
    },
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <SampleBadge
            hasSufficientData={row.original.hasSufficientData}
            sampleSize={row.original.sampleSize}
          />
        </div>
      );
    },
  },
  {
    id: "avgPromisScore",
    accessorKey: "avgPromisScore",
    header: () => {
      return <div className="w-full text-center">Avg Score</div>;
    },
    enableSorting: false,
    cell: ({ row }) => {
      return <div className="text-center">{row.original.avgPromisScore ?? "—"}</div>;
    },
  },
  {
    id: "avgDeltaFromBaseline",
    accessorKey: "avgDeltaFromBaseline",
    header: () => {
      return <div className="w-full text-center">Avg Δ from Baseline</div>;
    },
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <DeltaBadge
            avgDeltaFromBaseline={row.original.avgDeltaFromBaseline}
            hasSufficientData={row.original.hasSufficientData}
          />
        </div>
      );
    },
  },
];

const PromisPage = () => {
  const { queryBuilder, setSorting, sortingState } = useTableQueryBuilder({ filterKeys: [] });
  const { data, isLoading } = usePromis().getMetrics();
  const [filter, setFilter] = useState<FilterType>("all");

  const aggregates = data?.data ?? [];
  const totalUsers = data?.totalUsers ?? 0;
  const sufficientCount = aggregates.filter((a) => {
    return a.hasSufficientData;
  }).length;
  const lowNCount = aggregates.filter((a) => {
    return !a.hasSufficientData;
  }).length;

  const filteredData = aggregates.filter((a) => {
    if (filter === "sufficient") {
      return a.hasSufficientData;
    }
    if (filter === "low") {
      return !a.hasSufficientData;
    }

    return true;
  });

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <ScreenHeader>
        <ScreenTitle>PROMIS Metrics</ScreenTitle>
      </ScreenHeader>

      <ScreenContent>
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total users" value={totalUsers} />
          <StatCard label="Symptoms tracked" value={aggregates.length} />
          <StatCard
            label={`Sufficient data (N≥${env.VITE_PROMIS_MIN_SAMPLE_SIZE})`}
            value={sufficientCount}
          />
          <StatCard label="Low data symptoms" value={lowNCount} />
        </div>

        <SegmentedControl
          className="mb-4"
          label="Filter symptoms by data sufficiency"
          onChange={setFilter}
          options={FILTERS}
          value={filter}
        />

        <SinglePageTable
          columns={columns}
          data={filteredData}
          isLoadingData={isLoading}
          rowLink={({ symptomCode }) => {
            return {
              to: "/promis/$symptom-code",
              params: { "symptom-code": symptomCode },
            };
          }}
          rowsPerPage={20}
          setSorting={setSorting}
          sorting={sortingState}
        />
      </ScreenContent>
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/promis/")({
  component: PromisPage,
});
